import {
    HelpArticleItemCodeType, HelpArticleItemListType as HomeArticleItemListType,
    HelpArticleItemListType,
    HelpArticleItemType
} from "./help-article-constants";
import { useState } from "react";
import {
    faClipboard,
    faClipboardCheck,
    faCopy as fasCopy
} from "@fortawesome/sharp-solid-svg-icons";
import { faCopy } from "@fortawesome/sharp-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { HelpSubheading, HelpHeading, HelpText } from "../help-shared/help-container";
import { HelpTip } from "../help-shared/help-tip";
import "./help-article-item.css";

export function getTextContent(value) {
    const linkBegin = value.indexOf('!');
    if (linkBegin === -1) return value;
    const linkEnd = value.indexOf(')');
    if (linkEnd === -1) return value;
    const linkText = value.slice(linkBegin + 2, linkEnd);
    const splitIndex = linkText.indexOf('(');
    const linkPart = linkText.slice(0, splitIndex - 1);
    const textPart = linkText.slice(splitIndex + 1, linkText.length);
    const internalLink = linkPart.startsWith("help.");
    if (internalLink) {
        return (<>
            {value.slice(0, linkBegin)}
            <span className="link-blue-green cursor" >
                {textPart}
            </span>
            {value.slice(linkEnd + 1, value.length)}
        </>);
    }

    return (<>
        {value.slice(0, linkBegin)}
        <a href={linkPart} className="link-blue-green cursor" target="blank">
            {textPart}
        </a>
        {value.slice(linkEnd + 1, value.length)}
    </>);
}

function HelpArticleCodeCopyButton(props) {
    const getIcon = () => {
        if (props.Theme === "web") {
            return props.Copied ? fasCopy : faCopy;
        }
        return props.Copied ? faClipboardCheck : faClipboard;
    };

    const getLabel = () => {
        return props.Copied ? "Copied" : "Copy";
    };

    return (
        <div className={`eone-help-item-code-header-copy-${props.Theme}`} onClick={props.Copy}>
            <FontAwesomeIcon icon={getIcon()} fixedWidth={true} />
            <label className={`eone-help-item-code-header-copy-label-${props.Theme}`}>{getLabel()}</label>
        </div>
    );
}

function HelpArticleImage(props) {
    if (props.Theme === "sidebar" && props.HideInSidebar === true) {
        return null;
    }

    return (
        <div className="eone-help-item-image">
            <img src={`${props.BaseUrl}/images/${props.Value}`} alt={props.AltText} />
        </div>
    );
}

function HelpArticleList(props) {
    const getChildItems = () => {
        if (!props.Items) {
            return null;
        }

        return props.Items.map((item, index) => {
            return (
                <HelpArticleItem
                    key={index}
                    Type={item.type}
                    Value={item.value}
                    AltText={item.altText}
                    CodeType={item.codeType}
                    Items={item.Items}
                    Theme={props.Theme}
                    BaseUrl={props.BaseUrl} />
            );
        });
    };

    const getListClass = () => `eone-help-item-list-${props.Theme}`;

    if (props.ListType === HelpArticleItemListType.Number) {
        return (
            <ol className={getListClass()}>{getChildItems()}</ol>
        );
    } else {
        return (
            <ul className={getListClass()}>{getChildItems()}</ul>
        );
    }
}

function HelpArticleListItem(props) {
    return (
        <li className={`eone-help-item-list-item-${props.Theme}`}>{props.Value}</li>
    );
}

function HelpArticleVideo(props) {
    return (
        <div className="eone-help-item-video-container">
            <iframe src={props.Value} allowFullScreen />
        </div>
    );
}

function HelpArticleCode(props) {
    const [copied, setCopied] = useState(false);

    const copyToClipboard = () => {
        navigator.clipboard
            .writeText(props.Value)
            .then(() => {
                setCopied(true);
            })
            .then(() => {
                setTimeout(clearCopiedState, 5000);
            });
    };

    const clearCopiedState = () => {
        setCopied(false);
    };

    const getCodeTypeName = () => {
        switch (props.CodeType) {
            case HelpArticleItemCodeType.PowerShell:
                return "PowerShell"
            case HelpArticleItemCodeType.PopdockScript:
                return "Popdock script"
            case HelpArticleItemCodeType.Sql:
                return "SQL"
            case HelpArticleItemCodeType.Json:
                return "JSON"
            case HelpArticleItemCodeType.Xml:
                return "XML"
            default:
                return "";
        }
    };

    const formatJson = (jsonString) => {
        try {
            const parsedJSON = JSON.parse(jsonString);
            return JSON.stringify(parsedJSON, null, 4);
        } catch (error) {
            return jsonString;
        }
    }

    const getCodeText = () => {
        if (props.CodeType === HelpArticleItemCodeType.Json) {
            return formatJson(props.Value);
        }
        return props.Value;
    };

    return (
        <div className={`eone-help-item-code-container-${props.Theme}`}>
            <div className={`eone-help-item-code-header-${props.Theme}`}>
                <label className={`eone-help-code-header-label-${props.Theme}`}>{getCodeTypeName()}</label>
                <HelpArticleCodeCopyButton
                    Copied={copied}
                    Copy={copyToClipboard}
                    Theme={props.Theme} />
            </div>
            <div className={`eone-help-item-code-text-container-${props.Theme}`}>
                <pre className={`eone-help-item-code-text-${props.Theme}`}>{getCodeText()}</pre>
            </div>
        </div>
    );
}

function HelpArticleItem(props) {
    switch (props.Type) {
        case HelpArticleItemType.Title:
            return <HelpHeading {...props} />;
        case HelpArticleItemType.Subtitle:
            return <HelpSubheading {...props} />;
        case HelpArticleItemType.Text:
            return <HelpText Text={getTextContent(props.Value)} Theme={props.Theme} />;
        case HelpArticleItemType.Image:
            return <HelpArticleImage {...props} />;
        case HelpArticleItemType.Tip:
            return <HelpTip Tip={getTextContent(props.Value)} Theme={props.Theme} />;
        case HelpArticleItemType.ListItemGroup:
            return <HelpArticleList {...props} />;
        case HelpArticleItemType.ListItem:
            return <HelpArticleListItem {...props} />;
        case HelpArticleItemType.Video:
            return <HelpArticleVideo Value={props.Value} Theme={props.Theme} />;
        case HelpArticleItemType.Code:
            return <HelpArticleCode Value={props.Value} CodeType={props.CodeType} Theme={props.Theme} />;
        case HelpArticleItemType.Iframe:
            return (
                <div className={`eone-help-iframe-container-${props.Theme}`}>
                    <iframe src={props.Value}></iframe>
                </div>
            );
        case HelpArticleItemType.Link:
            //TODO: should href be set?    
            return (
                <div className="pd-link-primary">
                    <a>{props.Value}</a>
                </div>
            );
        default:
            return null;
    }
}

export function HelpArticleItems(props) {
    function createListGroup(listType, items) {
        return {
            type: HelpArticleItemType.ListItemGroup,
            listType: listType,
            Items: items
        };
    }

    function shouldStartNewNumberedList(item, currentListType, isListItem) {
        return currentListType === HomeArticleItemListType.Number &&
            isListItem &&
            item.listType === HomeArticleItemListType.Number &&
            item.startOfList === true;
    }

    function aggregateListItems(items) {
        const result = [];
        let listItems = [];
        let currentListType = null;
        let currentSubListType = null;
        let subListItems = [];

        for (const item of items) {
            const isListItem = item.type === HelpArticleItemType.ListItem;

            // Handle sub-list items
            if (currentSubListType) {
                if (item.type === HelpArticleItemType.ListItem && item.listType === currentSubListType) {
                    subListItems.push(item);
                    continue;
                }

                if (subListItems.length > 0) {
                    listItems.push(createListGroup(currentSubListType, subListItems));
                    subListItems = [];
                }

                currentSubListType = null;
            }

            // Start new list if no current list type
            if (isListItem && !currentListType) {
                currentListType = item.listType;
                listItems.push(item);
                continue;
            }

            // Handle starting a new numbered list when startOfList is true
            if (shouldStartNewNumberedList(item, currentListType, isListItem)) {
                if (listItems.length > 0) {
                    result.push(createListGroup(currentListType, listItems));
                    listItems = [];
                }
                currentListType = item.listType;
                listItems.push(item);
                continue;
            }

            // Handle numbered list items (including nested lists)
            if (currentListType === HomeArticleItemListType.Number) {
                if (isListItem && item.listType !== currentListType) {
                    currentSubListType = item.listType;
                    subListItems.push(item);
                } else {
                    listItems.push(item);
                }
                continue;
            }

            // Continue current list if same type
            if (isListItem && item.listType === currentListType) {
                listItems.push(item);
                continue;
            }

            // Finalize current list and add non-list item
            if (listItems.length > 0) {
                result.push(createListGroup(currentListType, listItems));
                listItems = [];
                currentListType = null;
            }
            result.push(item);
        }

        // Finalize any remaining list items
        if (listItems.length > 0) {
            result.push(createListGroup(currentListType, listItems));
        }

        return result;
    }
    
    const getItems = () => {
        const items = aggregateListItems(props.Items);
        return items.map((item, index) => {
            return (
                <HelpArticleItem
                    key={index}
                    Type={item.type}
                    ListType={item.listType}
                    Value={item.value}
                    AltText={item.altText}
                    CodeType={item.codeType}
                    StartOfList={item.startOfList}
                    HideInSidebar={item.hideInSidebar}
                    Items={item.Items}
                    Theme={props.Theme}
                    BaseUrl={props.BaseUrl} />
            );
        });
    };

    if (!props.Items || props.Items.length === 0) {
        return null;
    }

    return (
        <div className={`eone-help-items-container-${props.Theme}`}>
            {getItems()}
        </div>
    );
}